import { GET } from './executer';
import apiServiceInstance from './apiService';
import type { EventSource } from './eventsService';

/**
 * Event message entity
 */
export interface EventMessage {
  season_year: number;
  circuit_id: string;
  start_date: string;
  message: string;
  championship: string | null;
  created_at: string;
  value_date: string;
}

class MessagesService {
  /**
   * Fetch messages for a specific event using composite key
   * @param seasonYear The season year
   * @param circuitId The circuit ID
   * @param startDate The event start date (YYYY-MM-DD)
   * @param source The data source to fetch from ('prod', 'legacy', 'demo')
   */
  async getEventMessages(
    seasonYear: number, 
    circuitId: string, 
    startDate: string, 
    source: EventSource = 'prod'
  ) {
    const url = source === 'prod'
      ? `/api/messages/${seasonYear}/${circuitId}/${startDate}`
      : `/api/messages/${seasonYear}/${circuitId}/${startDate}?source=${source}`;
    
    return await apiServiceInstance.client.execute<EventMessage[]>(
      GET,
      url
    );
  }

  /**
   * Format timestamp for display in the circuit's local timezone
   * @param timestamp ISO 8601 UTC timestamp
   * @param timeZone IANA timezone (e.g., "Europe/Rome", "America/New_York")
   */
  formatTimestamp(timestamp: string, timeZone?: string): string {
    const date = new Date(timestamp);
    
    // If no timezone provided, use browser's local time (fallback)
    if (!timeZone) {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    }
    
    // Format in the circuit's timezone
    return date.toLocaleString('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  /**
   * Group messages by championship
   */
  groupMessagesByChampionship(messages: EventMessage[]): Record<string, EventMessage[]> {
    const grouped: Record<string, EventMessage[]> = {};
    
    messages.forEach(message => {
      const championship = message.championship || 'General';
      if (!grouped[championship]) {
        grouped[championship] = [];
      }
      grouped[championship].push(message);
    });
    
    return grouped;
  }
}

const messagesServiceInstance = new MessagesService();
export default messagesServiceInstance;

